import { T, useGT } from 'gt-react';
import { toast } from 'sonner';
import type { UploadLayout } from '@erp/rpc';

import { FileDropzone } from '../../item-master/components/FileDropzone';
import { RESULTS_ACCEPT, validateResultsFile } from './resultsFile';
import { SheetPicker } from './SheetPicker';
import { SupplierPicker } from './SupplierPicker';
import { ToolCard } from './ToolCard';

interface Props {
  fileName: string | null;
  isReading: boolean;
  layouts: UploadLayout[];
  supplierId: string | null;
  /** The sheet being converted, and those with a header row to pick from. */
  sheetName: string | null;
  sheetNames: string[];
  /** One file, or a supplier's two (e.g. Stelpro's RESULTS and Feedback). */
  onFiles: (files: File[]) => void;
  onSupplierChange: (layoutId: string) => void;
  onSheetChange: (sheetName: string) => void;
}

/** The first card: drop the supplier's results file, see which supplier it is. */
export function UploadCard({
  fileName,
  isReading,
  layouts,
  supplierId,
  sheetName,
  sheetNames,
  onFiles,
  onSupplierChange,
  onSheetChange,
}: Props) {
  const gt = useGT();
  return (
    <ToolCard
      title={<T context="heading of the upload card">Upload</T>}
      description={
        <T context="explanation under the upload card heading">
          Drop a supplier results workbook; you’ll be asked which supplier it is
          from.
        </T>
      }
    >
      <div className="flex h-full flex-col gap-2">
        <FileDropzone
          className="flex-1 px-3 py-4"
          accept={RESULTS_ACCEPT}
          disabled={isReading}
          prompt={
            <T context="instruction inside the Results to Upload drop area">
              Drop the supplier results .xlsx here, with its second file if the
              supplier sends two.
            </T>
          }
          validate={(file) => Promise.resolve(validateResultsFile(file, gt))}
          multiple
          onFilesAccepted={(files) => {
            // At most a supplier's two files; never guess which ones to keep.
            if (files.length > 2) {
              toast.error(
                gt(
                  'Drop one results file, or a supplier’s two files — not {count}.',
                  {
                    count: files.length,
                    context:
                      'toast when more than two files are dropped on Results → Upload',
                  },
                ),
              );
              return;
            }
            onFiles(files);
          }}
        />
        {fileName ? (
          <div className="flex flex-col gap-1">
            <span className="truncate text-sm font-medium" title={fileName}>
              {fileName}
            </span>
            {sheetNames.length > 1 ? (
              <SheetPicker
                sheetNames={sheetNames}
                value={sheetName}
                disabled={isReading}
                onChange={onSheetChange}
              />
            ) : null}
            <SupplierPicker
              layouts={layouts}
              value={supplierId}
              onChange={onSupplierChange}
            />
          </div>
        ) : null}
      </div>
    </ToolCard>
  );
}
